/**
 * Run route — shows an existing run via RunViewer, or a "new chat" view when
 * runId is "new" and a mateId search param is provided.
 *
 * The "new" flow reuses the exact same ChatView + TaskInputForm components as
 * RunViewer so the UI is identical. On first message it creates a Task + Run
 * via startChat({ mode: "task" }) and replaces the URL to /runs/$actualRunId.
 * The streaming store persists bubbles across navigation so RunViewer picks up.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatView } from "@/components/chat/ChatView";
import { RunViewer } from "@/components/run-viewer/RunViewer";
import { TaskInputForm } from "@/components/task-creation-form/task-input-form";
import { useTextareaAutoGrow } from "@/hooks/use-textarea-auto-grow";
import { useAgent } from "@/lib/api/hooks/tasks";
import { captureFrontendError } from "@/lib/errors/capture";
import { buildMetaTags } from "@/lib/seo/meta";
import { useBubbleStream } from "@/lib/streaming/use-bubble-stream";
import { useCombinedTools } from "@/lib/tasks/hooks/use-combined-tools";
import { useIntegrationIdMapping } from "@/lib/tasks/hooks/use-integration-id-mapping";
import { getNextToolsState } from "@/lib/utils/task-form";
import { useAuthStore } from "@/stores/auth-store";
import { usePageContextStore } from "@/stores/page-context-store";

export const Route = createFileRoute("/_dashboard/runs/$runId")({
	validateSearch: (search: Record<string, unknown>): { mateId?: number } => {
		const raw = search.mateId;
		const num = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : undefined;
		return { mateId: num && Number.isFinite(num) ? num : undefined };
	},

	head: () => ({
		meta: buildMetaTags({
			title: "Run History",
			description: "View run history and messages",
			noIndex: true,
			noFollow: true,
		}),
	}),

	component: RunPage,
});

function RunPage() {
	const { runId } = Route.useParams();
	const { mateId } = Route.useSearch();

	if (runId === "new" && mateId) {
		return <NewRunView mateId={mateId} />;
	}

	const runIdNumber = Number.parseInt(runId, 10);

	if (Number.isNaN(runIdNumber)) {
		return (
			<div className="flex-1 flex items-center justify-center p-6">
				<div className="text-center">
					<p className="text-lg font-semibold mb-2">Invalid Run ID</p>
					<p className="text-muted-foreground">The run ID "{runId}" is not valid.</p>
				</div>
			</div>
		);
	}

	return <RunViewer runId={runIdNumber} />;
}

/* -------------------------------------------------------------------------- */
/*  NewRunView — empty ChatView + TaskInputForm for first message to a mate   */
/* -------------------------------------------------------------------------- */

function NewRunView({ mateId }: { mateId: number }) {
	const navigate = useNavigate();
	const accessToken = useAuthStore((s) => s.accessToken);

	// Agent data
	const { data: agent, isLoading: isLoadingAgent } = useAgent(mateId);

	// Streaming
	const { startChat, isStreaming, runId: newRunId, stop: stopStream } = useBubbleStream();

	// Page header context
	const setPageContext = usePageContextStore((s) => s.setPageContext);
	const clearPageContext = usePageContextStore((s) => s.clearPageContext);
	useEffect(() => {
		setPageContext({
			title: "New Chat",
			subtitle: agent?.name,
			subtitleLink: agent ? `/teammates/${mateId}` : undefined,
		});
		return () => clearPageContext();
	}, [agent, mateId, setPageContext, clearPageContext]);

	// Navigate to actual run once streaming starts
	useEffect(() => {
		if (newRunId && isStreaming) {
			navigate({ to: "/runs/$runId", params: { runId: String(newRunId) }, replace: true });
		}
	}, [newRunId, isStreaming, navigate]);

	// Input state
	const [instructions, setInstructions] = useState("");
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [placeholderVisible, setPlaceholderVisible] = useState(true);
	const [permissionMode, setPermissionMode] = useState<"yolo" | "safe" | "plan">("safe");
	const [selectedTools, setSelectedTools] = useState<string[]>([]);
	const [isSending, setIsSending] = useState(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	useTextareaAutoGrow(textareaRef, 104, 300);

	// Tools
	const { userIntegrations, appIntegrations } = useIntegrationIdMapping({ enabled: !!agent });
	const combinedTools = useCombinedTools(userIntegrations, appIntegrations);

	useEffect(() => {
		if (instructions.trim()) setPlaceholderVisible(false);
		else setPlaceholderVisible(true);
	}, [instructions]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!instructions.trim() || !accessToken || !agent || isSending) return;

			const integrationIds =
				selectedTools.includes("auto") || selectedTools.length === 0
					? undefined
					: selectedTools.map(Number).filter((id) => Number.isFinite(id));

			setIsSending(true);
			try {
				await startChat({
					instanceId: agent.id,
					message: instructions.trim(),
					accessToken,
					mode: "task",
					permissionMode,
					integrationIds,
					files: selectedFiles.length > 0 ? selectedFiles : undefined,
				});
				setInstructions("");
				setSelectedFiles([]);
			} catch (error) {
				captureFrontendError(error, {
					area: "new_run",
					action: "start_chat",
					agentId: agent.id,
				});
				toast.error("Failed to start chat. Please try again.");
				setIsSending(false);
			}
		},
		[
			instructions,
			accessToken,
			agent,
			isSending,
			startChat,
			permissionMode,
			selectedTools,
			selectedFiles,
		]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
				e.preventDefault();
				handleSubmit({ preventDefault: () => {} } as React.FormEvent);
			}
		},
		[handleSubmit]
	);

	const handleToggleTool = useCallback((toolId: string) => {
		setSelectedTools((prev) => getNextToolsState(prev, toolId, false));
	}, []);

	// Agent not found
	if (!isLoadingAgent && !agent) {
		return (
			<div className="flex-1 flex items-center justify-center p-6">
				<p className="text-muted-foreground">Teammate not found.</p>
			</div>
		);
	}

	// Agent must be enabled — backend also enforces this (400 if disabled)
	if (agent && agent.status !== "enabled") {
		return (
			<div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
				<p className="text-muted-foreground">
					This teammate is {agent.status === "disabled" ? "paused" : "archived"} and cannot receive
					messages.
				</p>
			</div>
		);
	}

	return (
		<ChatView bubbles={[]} agentName={agent?.name || "Agent"} isLoading={isLoadingAgent}>
			<TaskInputForm
				instructions={instructions}
				placeholder={`Message ${agent?.name || "teammate"}...`}
				isStreaming={isStreaming}
				placeholderVisible={placeholderVisible}
				selectedTools={selectedTools}
				combinedTools={combinedTools}
				textareaRef={textareaRef}
				onSubmit={handleSubmit}
				onInstructionsChange={setInstructions}
				onKeyDown={handleKeyDown}
				onFocus={() => {}}
				onBlur={() => {}}
				onToggleTool={handleToggleTool}
				onStop={() => stopStream({ cancelRun: true })}
				autoFocus
				variant="default"
				selectedFiles={selectedFiles}
				onFilesChange={setSelectedFiles}
				permissionMode={permissionMode}
				onPermissionModeChange={setPermissionMode}
				showAutoOption={false}
				disabled={isLoadingAgent || isSending}
				disabledMessage={isLoadingAgent ? "Loading teammate..." : undefined}
			/>
		</ChatView>
	);
}
